/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useState } from 'react';
import { StyleSheet, Switch, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors } from '../../../../shared/theme/ticketColors';
import { spacing } from '../../../../shared/theme/spacing';
import { typography } from '../../../../shared/theme/typography';
import { showError, showSuccess } from '../../../../shared/ui/appToast';
import { listCommissionPlans } from '../../../../commissions/infra/commissionsRepo';
import type { CommissionPlan } from '../../../../commissions/domain/commissionTypes';
import { updateEmployeeCommissionSettings } from '../../../application/employeeCommissionSettings';

type Props = {
  /** employees.auth_user_id — requerido por RPCs de Comisiones. */
  authUserId: string;
  earnsCommission: boolean;
  commissionPlanId: string | null;
  /** Refresca el empleado tras un cambio persistido (ej. reload desde la store). */
  onChanged?: () => void;
};

export function EmployeeCommissionSection({
  authUserId,
  earnsCommission,
  commissionPlanId,
  onChanged,
}: Props) {
  const [plans, setPlans] = useState<CommissionPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPlansLoading(true);
    setPlansError(null);
    listCommissionPlans(null)
      .then((list) => {
        if (cancelled) return;
        setPlans(list);
      })
      .catch(() => {
        if (cancelled) return;
        setPlansError('No se pudieron cargar los planes.');
        setPlans([]);
      })
      .finally(() => {
        if (!cancelled) setPlansLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = useCallback(
    async (params: { earnsCommission?: boolean; commissionPlanId?: string | null }) => {
      if (!authUserId) {
        showError('No se pudo actualizar', 'No encontramos la cuenta de esta persona.');
        return;
      }
      setSaving(true);
      try {
        await updateEmployeeCommissionSettings(authUserId, {
          ...params,
          currentPlanId: commissionPlanId,
        });
        showSuccess('Comisiones actualizadas');
        onChanged?.();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Intentá de nuevo.';
        showError('No se pudo actualizar', msg);
      } finally {
        setSaving(false);
      }
    },
    [authUserId, commissionPlanId, onChanged]
  );

  const handleToggleEarns = (value: boolean) => {
    if (saving) return;
    void persist({ earnsCommission: value });
  };

  const handleSelectPlan = (planId: string) => {
    if (saving) return;
    if (planId === commissionPlanId) return;
    void persist({ commissionPlanId: planId });
  };

  const handleClearPlan = () => {
    if (saving) return;
    if (!commissionPlanId) return;
    void persist({ commissionPlanId: null });
  };

  return (
    <View style={styles.wrap} testID="perfil.team.detail.section.comisiones">
      <View style={styles.earnsRow}>
        <View style={styles.earnsCopy}>
          <Text style={styles.earnsTitle}>Genera comisiones</Text>
          <Text style={styles.earnsHint}>
            Activá para que este empleado acumule comisiones por sus ventas.
          </Text>
        </View>
        <Switch
          value={earnsCommission}
          onValueChange={handleToggleEarns}
          trackColor={{ false: ticketColors.rule, true: ticketColors.gold }}
          thumbColor={ticketColors.paper}
          disabled={saving || !authUserId}
          accessibilityLabel="Genera comisiones"
          testID="perfil.team.detail.earns-commission"
        />
      </View>

      {earnsCommission ? (
        <View style={styles.planBlock}>
          <Text style={styles.planLabel}>Plan de comisión</Text>
          {plansError ? (
            <Text style={styles.planHint}>{plansError}</Text>
          ) : plansLoading ? (
            <Text style={styles.planHint}>Cargando planes…</Text>
          ) : plans.length === 0 ? (
            <Text style={styles.planHint}>
              No hay planes creados. Creá uno desde Comisiones para asignarlo.
            </Text>
          ) : (
            <View style={styles.planChips}>
              {commissionPlanId ? (
                <TouchableOpacity
                  style={[styles.planChip, styles.planChipClear]}
                  onPress={handleClearPlan}
                  disabled={saving}
                  accessibilityRole="button"
                  accessibilityLabel="Quitar plan asignado"
                  testID="perfil.team.detail.commission-plan-clear"
                >
                  <Ionicons name="close" size={12} color={ticketColors.ink3} />
                  <Text style={styles.planChipClearLabel}>Sin plan</Text>
                </TouchableOpacity>
              ) : null}
              {plans.map((plan) => {
                const active = plan.id === commissionPlanId;
                return (
                  <TouchableOpacity
                    key={plan.id}
                    style={[styles.planChip, active && styles.planChipActive]}
                    onPress={() => handleSelectPlan(plan.id)}
                    disabled={saving}
                    accessibilityRole="button"
                    accessibilityLabel={`Asignar plan ${plan.name}`}
                    testID={`perfil.team.detail.commission-plan-${plan.id}`}
                  >
                    <Text
                      style={[styles.planChipLabel, active && styles.planChipLabelActive]}
                      numberOfLines={1}
                    >
                      {plan.name}
                    </Text>
                    {active ? (
                      <Ionicons name="checkmark" size={14} color={ticketColors.gold} />
                    ) : null}
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.sm,
  },
  earnsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
  },
  earnsCopy: {
    flex: 1,
    gap: 2,
  },
  earnsTitle: {
    ...typography.body,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 13,
  },
  earnsHint: {
    ...typography.caption,
    color: ticketColors.ink2,
    lineHeight: 16,
  },
  planBlock: {
    gap: spacing.xs,
  },
  planLabel: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '700',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.35,
  },
  planHint: {
    ...typography.caption,
    color: ticketColors.ink3,
  },
  planChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  planChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    minHeight: 36,
  },
  planChipActive: {
    borderColor: ticketColors.gold + '50',
    backgroundColor: ticketColors.gold + '10',
  },
  planChipLabel: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '700',
    fontSize: 12,
  },
  planChipLabelActive: {
    color: ticketColors.gold,
  },
  planChipClear: {
    borderColor: ticketColors.rule,
    backgroundColor: ticketColors.paper,
  },
  planChipClearLabel: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '600',
    fontSize: 12,
  },
});
